/* ============================================================
   Nerosà London — Cart Drawer JS  (Option 2: Bundle Variants)
   File: assets/nerosa-cart-drawer.js

   Bundle detection: checks item.properties._bundle_id
   Saving display: reads _bundle_saving and _bundle_rrp from properties
   Multiple bundles: each is its own line item — all handled correctly
   ============================================================ */

(function () {
  'use strict';

  var SHIPPING_PENCE   = 399;
  var FREE_THRESHOLD   = 4000;
  var BESTSELLERS_HANDLE = 'bestsellers';

  /* ── State ──────────────────────────────────────────────────── */
  var _cartData    = null;
  var _bestsellers = null;
  var _isOpen      = false;
  var _fetching    = false;

  /* ── DOM refs ───────────────────────────────────────────────── */
  var $drawer, $overlay, $items, $loading, $empty,
      $subtotal, $shippingEl, $discountRow, $discountLabel, $discountVal,
      $footer, $summaryWrap, $shippingText, $checkoutTotal,
      $upsellWrap, $upsellTrack, $bundlePromo;

  /* ── Currency ───────────────────────────────────────────────── */
  function fmt(pence) { return '\u00a3' + (pence / 100).toFixed(2); }

  /* ── Detect if a line item is a bundle ─────────────────────── */
  function isBundleItem(item) {
    return !!(item.properties && item.properties['_bundle_id']);
  }

  /* ── Open / close ───────────────────────────────────────────── */
  function openDrawer() {
    if (_isOpen) return;
    _isOpen = true;
    $drawer.removeAttribute('hidden');
    $drawer.getBoundingClientRect();
    $drawer.classList.add('is-open');
    $overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    $drawer.focus();
    fetchCart();
  }

  function closeDrawer() {
    _isOpen = false;
    $drawer.classList.remove('is-open');
    $overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    setTimeout(function () { if (!_isOpen) $drawer.setAttribute('hidden', ''); }, 380);
  }

  /* ── Fetch cart ─────────────────────────────────────────────── */
  function fetchCart() {
    if (_fetching) return;
    _fetching = true;
    $loading.style.display = 'flex';
    clearItems();

    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        _cartData  = cart;
        _fetching  = false;
        $loading.style.display = 'none';
        renderCart(cart);
        if (!_bestsellers) fetchBestsellers(cart);
      })
      .catch(function () { _fetching = false; $loading.style.display = 'none'; });
  }

  /* ── Render cart ────────────────────────────────────────────── */
  function renderCart(cart) {
    clearItems();

    if (!cart.items || !cart.items.length) {
      $empty.style.display       = 'block';
      $summaryWrap.style.display = 'none';
      $footer.style.display      = 'none';
      showBundlePromo(true);
      updateShippingMsg(0);
      updateBadges(0);
      /* Fix #4: rename "Shop Perfumes" → "Shop All" */
      var shopBtn = $empty ? $empty.querySelector('.ncd-empty-cta') : null;
      if (shopBtn) shopBtn.textContent = 'Shop All';
      return;
    }

    $empty.style.display       = 'none';
    $summaryWrap.style.display = 'block';
    $footer.style.display      = 'block';

    /* ── Separate bundle items from regular items ─────────────── */
    var bundleItems  = cart.items.filter(isBundleItem);
    var regularItems = cart.items.filter(function (i) { return !isBundleItem(i); });
    var hasBundle    = bundleItems.length > 0;

    /* ── Calculate total bundle saving across all bundle items ── */
    var totalBundleSaving = 0;
    bundleItems.forEach(function (item) {
      var s = item.properties['_bundle_saving'] ? parseInt(item.properties['_bundle_saving'], 10) : 0;
      totalBundleSaving += s * item.quantity;
    });

    /* Hide "build a bundle" promo strip when bundle is in cart */
    showBundlePromo(!hasBundle);

    /* ── Render items ─────────────────────────────────────────── */
    var frag = document.createDocumentFragment();

    /* Bundle items first, with group header per distinct bundle */
    if (bundleItems.length) {
      var bundleHeader = document.createElement('div');
      bundleHeader.className = 'ncd-bundle-group-header';
      bundleHeader.innerHTML =
        '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">' +
          '<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>' +
        '</svg>' +
        '<span>' + (bundleItems.length === 1 ? 'Your bundle' : 'Your bundles') + '</span>' +
        '<div class="ncd-bundle-group-line"></div>';
      frag.appendChild(bundleHeader);
      bundleItems.forEach(function (item) { frag.appendChild(buildBundleItem(item)); });
    }

    regularItems.forEach(function (item) { frag.appendChild(buildRegularItem(item)); });
    $items.appendChild(frag);

    /* ── Summary ─────────────────────────────────────────────── */
    var cartTotal = cart.total_price; /* already correct — bundle variant is priced right */

    if (hasBundle && totalBundleSaving > 0) {
      /* Show "you saved X" informational row */
      if ($discountRow) {
        $discountRow.style.display = 'flex';
        if ($discountLabel) $discountLabel.textContent = 'Bundle saving';
        if ($discountVal)   $discountVal.textContent   = '\u2212' + fmt(totalBundleSaving);
      }
    } else {
      if ($discountRow) $discountRow.style.display = 'none';
    }

    /* Shipping: free if bundle present */
    var shippingFree = hasBundle || cartTotal >= FREE_THRESHOLD;
    if ($shippingEl) $shippingEl.textContent = shippingFree ? 'FREE' : fmt(SHIPPING_PENCE);
    updateShippingMsg(shippingFree ? FREE_THRESHOLD : cartTotal);

    var checkoutTotal = cartTotal + (shippingFree ? 0 : SHIPPING_PENCE);
    if ($subtotal)      $subtotal.textContent      = fmt(cartTotal);
    if ($checkoutTotal) $checkoutTotal.textContent = fmt(checkoutTotal);

    updateBadges(cart.item_count);
  }

  /* ── Build a bundle line item ───────────────────────────────── */
  function buildBundleItem(item) {
    var el = document.createElement('div');
    el.className = 'ncd-item ncd-item--bundle';
    el.dataset.key = item.key;

    /* Image */
    var imgHtml = item.image
      ? '<img class="ncd-item-img" src="' + item.image.replace(/(\.[a-z]+)$/, '_120x$1') + '" alt="' + esc(item.title) + '" loading="lazy" width="72" height="88">'
      : '<div class="ncd-item-img-placeholder"></div>';

    /* Scent names from visible properties */
    var scents = [];
    for (var key in item.properties) {
      if (item.properties.hasOwnProperty(key) && key.indexOf('Scent ') === 0) {
        scents.push(item.properties[key]);
      }
    }
    var scentLine = scents.length ? scents.join(' &amp; ') : '';

    /* Bundle size label from hidden property */
    var bundleSize = item.properties['_bundle_size'] || '';
    var sizeLabel  = bundleSize === '2' ? 'Duo' : bundleSize === '3' ? 'Trio' : '';

    /* Saving per unit */
    var savingPence = item.properties['_bundle_saving'] ? parseInt(item.properties['_bundle_saving'], 10) : 0;
    var savingHtml  = savingPence > 0
      ? '<div class="ncd-bundle-saving">You\'ve unlocked ' + fmt(savingPence) + ' in savings!</div>'
      : '';

    el.innerHTML =
      imgHtml +
      '<div class="ncd-item-info">' +
        '<div class="ncd-item-top">' +
          '<span class="ncd-item-name">' + esc(item.product_title || item.title) + '</span>' +
          '<span class="ncd-item-price">' + fmt(item.final_line_price) + '</span>' +
        '</div>' +
        (sizeLabel ? '<div class="ncd-item-meta">' + sizeLabel + ' bundle \u00b7 Eau de Parfum 50ml</div>' : '') +
        (scentLine ? '<div class="ncd-item-scents">' + scentLine + '</div>' : '') +
        savingHtml +
        '<div class="ncd-item-controls">' +
          '<div class="ncd-qty">' +
            '<button class="ncd-qty-btn" data-key="' + item.key + '" data-qty="' + (item.quantity - 1) + '" aria-label="Remove one bundle">\u2212</button>' +
            '<span class="ncd-qty-num">' + item.quantity + '</span>' +
            '<button class="ncd-qty-btn" data-key="' + item.key + '" data-qty="' + (item.quantity + 1) + '" aria-label="Add another bundle">+</button>' +
          '</div>' +
          '<button class="ncd-remove-btn" data-key="' + item.key + '" aria-label="Remove bundle">' +
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>' +
          '</button>' +
        '</div>' +
      '</div>';

    return el;
  }

  /* ── Build a regular (non-bundle) line item ─────────────────── */
  function buildRegularItem(item) {
    var el = document.createElement('div');
    el.className = 'ncd-item';
    el.dataset.key = item.key;

    var imgHtml = item.image
      ? '<img class="ncd-item-img" src="' + item.image.replace(/(\.[a-z]+)$/, '_120x$1') + '" alt="' + esc(item.title) + '" loading="lazy" width="72" height="88">'
      : '<div class="ncd-item-img-placeholder"></div>';

    var inspiredBy = item.properties && item.properties['Inspired by'] ? item.properties['Inspired by'] : '';
    var sizeLabel  = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
    var metaLine   = 'Eau de Parfum' + (sizeLabel ? ' \u00b7 ' + sizeLabel : '');

    el.innerHTML =
      imgHtml +
      '<div class="ncd-item-info">' +
        '<div class="ncd-item-top">' +
          '<span class="ncd-item-name">' + esc(item.product_title || item.title) + '</span>' +
          '<span class="ncd-item-price">' + fmt(item.final_line_price) + '</span>' +
        '</div>' +
        '<div class="ncd-item-meta">' + esc(metaLine) + '</div>' +
        (inspiredBy ? '<div class="ncd-item-inspired">Inspired by ' + esc(inspiredBy) + '</div>' : '') +
        '<div class="ncd-item-controls">' +
          '<div class="ncd-qty">' +
            '<button class="ncd-qty-btn" data-key="' + item.key + '" data-qty="' + (item.quantity - 1) + '" aria-label="Decrease quantity">\u2212</button>' +
            '<span class="ncd-qty-num">' + item.quantity + '</span>' +
            '<button class="ncd-qty-btn" data-key="' + item.key + '" data-qty="' + (item.quantity + 1) + '" aria-label="Increase quantity">+</button>' +
          '</div>' +
          '<button class="ncd-remove-btn" data-key="' + item.key + '" aria-label="Remove item">' +
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>' +
          '</button>' +
        '</div>' +
      '</div>';

    return el;
  }

  /* ── Update quantity ────────────────────────────────────────── */
  var _qtyTimer = null;
  function updateQty(key, qty) {
    clearTimeout(_qtyTimer);
    _qtyTimer = setTimeout(function () {
      fetch('/cart/change.js', {
        method:      'POST',
        credentials: 'same-origin',
        headers:     { 'Content-Type': 'application/json' },
        body:        JSON.stringify({ id: key, quantity: Math.max(0, qty) })
      })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        _cartData = cart;
        renderCart(cart);
        updateBadges(cart.item_count);
        if (_bestsellers) renderUpsells(_bestsellers, cart);
      })
      .catch(function (err) { console.error('Qty update error:', err); });
    }, 120);
  }

  /* ── Bundle promo strip ─────────────────────────────────────── */
  function showBundlePromo(show) {
    if ($bundlePromo) $bundlePromo.style.display = show ? 'flex' : 'none';
  }

  /* ── Shipping message ───────────────────────────────────────── */
  function updateShippingMsg(subtotalPence) {
    if (!$shippingText) return;
    if (subtotalPence >= FREE_THRESHOLD) {
      $shippingText.textContent = 'You\'ve unlocked FREE shipping!';
    } else {
      var rem = FREE_THRESHOLD - subtotalPence;
      $shippingText.textContent = 'You\'re ' + fmt(rem) + ' away from FREE shipping';
    }
  }

  /* ── Nav badge ──────────────────────────────────────────────── */
  function updateBadges(count) {
    document.querySelectorAll('.nerosa-cart-count, .ncd-badge').forEach(function (el) {
      el.textContent = count > 0 ? String(count) : '';
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  /* ── Clear items ────────────────────────────────────────────── */
  function clearItems() {
    $items.querySelectorAll('.ncd-item, .ncd-bundle-group-header').forEach(function (el) { el.remove(); });
  }

  /* ── Bestsellers / upsells ──────────────────────────────────── */
  function fetchBestsellers(cart) {
    fetch('/collections/' + BESTSELLERS_HANDLE + '/products.json?limit=20', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        _bestsellers = data.products || [];
        if (!_bestsellers.length) fetchFallbackProducts(cart);
        else renderUpsells(_bestsellers, cart);
      })
      .catch(function () { fetchFallbackProducts(cart); });
  }

  function fetchFallbackProducts(cart) {
    fetch('/products.json?limit=20', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) { _bestsellers = data.products || []; renderUpsells(_bestsellers, cart); })
      .catch(function () {});
  }

  function renderUpsells(products, cart) {
    if (!$upsellTrack || !products.length) return;
    /* Don't suggest bundle products as upsells */
    var cartVariantIds = (_cartData ? _cartData.items : []).map(function (i) { return i.variant_id; });
    var filtered = products.filter(function (p) {
      var vid = p.variants && p.variants[0] && p.variants[0].id;
      /* Skip if already in cart or if it's a bundle product */
      var isBundle = (p.title || '').toLowerCase().indexOf('bundle') > -1;
      return !isBundle && cartVariantIds.indexOf(vid) === -1;
    }).slice(0, 6);

    if (!filtered.length) { if ($upsellWrap) $upsellWrap.style.display = 'none'; return; }
    if ($upsellWrap) $upsellWrap.style.display = 'block';
    $upsellTrack.innerHTML = '';
    filtered.forEach(function (p) { $upsellTrack.appendChild(buildUpsellCard(p)); });
  }

  /* Split "PRODUCT NAME – INSPIRED BY VALUE" into {name, inspiredBy}.
     Uses indexOf (not regex) to handle any dash character Shopify may use. */
  function extractProductParts(fullTitle) {
    if (!fullTitle) return { name: '', inspiredBy: '' };

    var lower = fullTitle.toLowerCase();
    var marker = 'inspired by ';
    var idx = lower.indexOf(marker);

    if (idx > 2) {
      /* Remove trailing separator chars (–, —, -, spaces) from the name */
      var name = fullTitle.substring(0, idx).replace(/[\s\u2010-\u2015\u2212\-]+$/, '').trim();
      var inspiredBy = fullTitle.substring(idx + marker.length).trim();
      if (name && inspiredBy) return { name: name, inspiredBy: inspiredBy };
    }
    return { name: fullTitle, inspiredBy: '' };
  }

  function buildUpsellCard(product) {
    var card = document.createElement('div');
    card.className = 'ncd-upsell-card';

    var variant  = product.variants && product.variants[0];
    var varId    = variant ? variant.id : null;
    var rawPrice = variant ? variant.price : 0;
    var priceStr = '\u00a3' + parseFloat(rawPrice || 0).toFixed(2);

    /* Split title to get clean name + inspired-by */
    var parts       = extractProductParts(product.title);
    var displayName = parts.name;

    /* Prefer the global lookup (populated from Liquid metafields in the bundle
       page). Fall back to title extraction for products with "– Inspired by"
       in their Shopify title. */
    var lookupInspo = window.nerosaInspirations && window.nerosaInspirations[product.handle];
    var rawInspo    = lookupInspo || parts.inspiredBy;
    var inspiredBy  = rawInspo ? 'Inspired by ' + rawInspo : '';

    console.log('[Nerosà Upsell]', product.handle, '→', displayName, '| inspiredBy:', rawInspo || '(none)');

    /* Image — Shopify size modifier */
    var imgSrc = product.images && product.images[0]
      ? product.images[0].src.replace(/(\.[a-z]+)(\?[^?]*)?$/i, '_280x$1')
      : null;
    var imgHtml = imgSrc
      ? '<img src="' + imgSrc + '" alt="' + esc(displayName) + '" loading="lazy">'
      : '';

    card.innerHTML =
      '<div class="ncd-upsell-img-wrap">' + imgHtml + '</div>' +
      '<div class="ncd-upsell-stars"></div>' +
      '<div class="ncd-upsell-text">' +
        '<div class="ncd-upsell-name">' + esc(displayName) + '</div>' +
        '<div class="ncd-upsell-inspired">' + esc(inspiredBy) + '</div>' +
        '<div class="ncd-upsell-price">' + priceStr + '</div>' +
      '</div>' +
      '<button class="ncd-upsell-add" data-variant-id="' + varId + '">Add to cart</button>';

    return card;
  }

  /* ── Event binding ──────────────────────────────────────────── */
  function bindEvents() {
    document.addEventListener('click', function (e) {
      /* Open drawer */
      if (e.target.closest('#nerosa-cart-btn, #nerosa-mob-cart-btn, [data-cart-trigger]')) {
        e.preventDefault(); e.stopPropagation(); openDrawer(); return;
      }
      /* Close */
      if (e.target.closest('#ncd-close') || e.target === $overlay) { closeDrawer(); return; }
      /* Qty */
      var qtyBtn = e.target.closest('.ncd-qty-btn');
      if (qtyBtn) { updateQty(qtyBtn.dataset.key, parseInt(qtyBtn.dataset.qty, 10)); return; }
      /* Remove */
      var rmBtn = e.target.closest('.ncd-remove-btn');
      if (rmBtn) { updateQty(rmBtn.dataset.key, 0); return; }
      /* Upsell add */
      var upsellBtn = e.target.closest('.ncd-upsell-add');
      if (upsellBtn) {
        fetch('/cart/add.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: upsellBtn.dataset.variantId, quantity: 1 })
        })
        .then(function (r) { return r.json(); })
        .then(function () { fetchCart(); })
        .catch(function () {});
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && _isOpen) closeDrawer();
    });

    /* Intercept native cart adds (product page ATC) */
    var origFetch = window.fetch;
    window.fetch = function () {
      var args = arguments;
      return origFetch.apply(this, args).then(function (res) {
        var url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
        if (url.indexOf('/cart/add') > -1) {
          setTimeout(function () {
            origFetch('/cart.js', { credentials: 'same-origin' })
              .then(function (r) { return r.json(); })
              .then(function (cart) {
                _cartData = cart;
                updateBadges(cart.item_count);
                if (_isOpen) renderCart(cart);
              })
              .catch(function () {});
          }, 350);
        }
        return res;
      });
    };
  }

  /* ── Escape HTML ────────────────────────────────────────────── */
  function esc(str) {
    return String(str || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }

  /* ── Init ───────────────────────────────────────────────────── */
  function init() {
    $drawer = document.getElementById('ncd-drawer');
    if (!$drawer) return;

    var bh = $drawer.dataset.bestsellersHandle;
    var sp = parseInt($drawer.dataset.shippingPence, 10);
    var ft = parseInt($drawer.dataset.freeThreshold,  10);
    if (bh)       BESTSELLERS_HANDLE = bh;
    if (!isNaN(sp)) SHIPPING_PENCE   = sp;
    if (!isNaN(ft)) FREE_THRESHOLD   = ft;

    $overlay       = document.getElementById('ncd-overlay');
    $items         = document.getElementById('ncd-items');
    $loading       = document.getElementById('ncd-loading');
    $empty         = document.getElementById('ncd-empty');
    $subtotal      = document.getElementById('ncd-subtotal');
    $shippingEl    = document.getElementById('ncd-shipping');
    $discountRow   = document.getElementById('ncd-discount-row');
    $discountLabel = document.querySelector('#ncd-discount-row .ncd-discount-label');
    $discountVal   = document.getElementById('ncd-discount-val');
    $footer        = document.getElementById('ncd-footer');
    $summaryWrap   = document.getElementById('ncd-summary-wrap');
    $shippingText  = document.getElementById('ncd-shipping-text');
    $checkoutTotal = document.getElementById('ncd-checkout-total');
    $upsellWrap    = document.getElementById('ncd-upsell');
    $upsellTrack   = document.getElementById('ncd-upsell-track');
    $bundlePromo   = document.querySelector('.ncd-bundle-promo');

    bindEvents();
  }

  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', init); }
  else { init(); }

})();
